/** @module analyses/findKeywordInFirstParagraph */
import {inRange, isEmpty} from 'lodash';

import {findTopicFormsInString} from '../helpers/match/findKeywordFormsInString.js';
import {getParentNode} from '../helpers/sentence/getSentencesFromTree';
import {createShortcodeTagsRegex} from '../helpers';
import isChineseText from '../helpers/language/isChineseText';

// Matches common UI-marker class names (label, eyebrow, kicker, etc.) used for
// short text placed above a heading. Anchored to the start, with a boundary so
// `header-label` or `tagline` don't match.
const LABEL_LIKE_CLASS_REGEX = /^(label|eyebrow|kicker|overline|pretitle|subtitle|badge|tag|chip|meta|category|breadcrumb)(\b|[-_])/i;
const MIN_INTRO_WORDS = 5;

/**
 * Checks if the introductory paragraph contains keyphrase or synonyms.
 * First splits the first paragraph by sentences. Finds the first paragraph which contains sentences e.g., not an image).
 * (1) Tries to find all (content) words from the keyphrase or a synonym phrase within one sentence.
 * If found all words within one sentence, returns an object with foundInOneSentence = true and keyphraseOrSynonym = "keyphrase"
 * or "synonym".
 * If it did not find all words within one sentence, goes ahead with matching the keyphrase with the entire first paragraph.
 * (2) Tries to find all (content) words from the keyphrase or a synonym phrase within the paragraph.
 * If found all words within the paragraph, returns an object with foundInOneSentence = false, foundInParagraph = true,
 * and keyphraseOrSynonym = "keyphrase" or "synonym".
 * If found not all words within the paragraph of nothing at all, returns an object with foundInOneSentence = false,
 * foundInParagraph = false, and keyphraseOrSynonym = "".
 *
 * @param {Paper} paper The text to check for paragraphs.
 * @param {Researcher} researcher The researcher to use for analysis.
 *
 * @returns {Object} Whether the keyphrase words were found in one sentence, whether the keyphrase words were found in
 * the paragraph, whether a keyphrase or a synonym phrase was matched.
 */
export default function(paper, researcher) {
  let paragraphs = researcher.getResearch('getParagraphs');
  // Filter captions from non-Classic editors.
  paragraphs = paragraphs.filter(paragraph => {
    const parentNode = getParentNode(paper, paragraph);
    return !(paragraph.isImplicit && parentNode && parentNode.name === 'figcaption');
  });
  // Filter captions from Classic editor and from classic block inside Block editor.
  paragraphs = paragraphs.filter(paragraph => {
    return !(
      paragraph.childNodes &&
      paragraph.childNodes[0] &&
      createShortcodeTagsRegex(['caption']).test(paragraph.childNodes[0].value)
    );
  });
  // Filter UI label/eyebrow/kicker paragraphs that sit before the heading
  // (e.g., <p class="label">CATEGORY</p>). These are visual markers, not real
  // intro paragraphs, and would cause false negatives on keyword-in-intro checks.
  paragraphs = paragraphs.filter(paragraph => {
    const classSet = paragraph.attributes && paragraph.attributes.class;
    if (classSet instanceof Set) {
      for (const cls of classSet) {
        if (LABEL_LIKE_CLASS_REGEX.test(cls)) {
          return false;
        }
      }
    }
    const wordCount = (paragraph.innerText() || '')
      .trim()
      .split(/\s+/)
      .filter(Boolean).length;
    return wordCount >= MIN_INTRO_WORDS;
  });
  const firstParagraph = paragraphs[0];

  const topicForms = researcher.getResearch('morphology');
  let matchWordCustomHelper = researcher.getHelper('matchWordCustomHelper');
  const locale = paper.getLocale();

  // Auto-detect Chinese and use Chinese helper if not already available
  if (
    !matchWordCustomHelper &&
    isChineseText(paper.getKeyword() + ' ' + (firstParagraph ? firstParagraph.innerText() : ''))
  ) {
    // Use Chinese word matching for Chinese text
    matchWordCustomHelper = function(text, word) {
      const textToSearch = typeof text === 'string' ? text : text.text || text;
      const matches = [];

      if (!textToSearch || !word) {
        return matches;
      }

      const lowerText = textToSearch.toLowerCase();
      const lowerWord = word.toLowerCase();

      let startIndex = 0;
      let index;

      while ((index = lowerText.indexOf(lowerWord, startIndex)) !== -1) {
        matches.push(word);
        startIndex = index + lowerWord.length;
      }

      return matches;
    };
  }
  const startOffset = firstParagraph && firstParagraph.sourceCodeLocation.startOffset;

  const mappedBlocks = paper._attributes.wpBlocks;
  const filteredIntroductionBlock =
    mappedBlocks &&
    mappedBlocks.filter(block => inRange(startOffset, block.startOffset, block.endOffset))[0];
  const result = {
    foundInOneSentence: false,
    foundInParagraph: false,
    keyphraseOrSynonym: '',
    introduction: firstParagraph,
    parentBlock: filteredIntroductionBlock || null
  };

  if (isEmpty(firstParagraph)) {
    return result;
  }

  const sentences = firstParagraph.sentences.map(sentence => sentence.text);
  // Use both keyphrase and synonyms to match topic words in the first paragraph.
  const useSynonyms = true;

  if (!isEmpty(sentences)) {
    const firstResultSentence = sentences
      .map(sentence =>
        findTopicFormsInString(topicForms, sentence, useSynonyms, locale, matchWordCustomHelper)
      )
      .find(resultSentence => resultSentence.percentWordMatches === 100);

    if (firstResultSentence) {
      result.foundInOneSentence = true;
      result.foundInParagraph = true;
      result.keyphraseOrSynonym = firstResultSentence.keyphraseOrSynonym;
      return result;
    }

    const resultParagraph = findTopicFormsInString(
      topicForms,
      firstParagraph.innerText(),
      useSynonyms,
      locale,
      matchWordCustomHelper
    );
    if (resultParagraph.percentWordMatches === 100) {
      result.foundInParagraph = true;
      result.keyphraseOrSynonym = resultParagraph.keyphraseOrSynonym;
      return result;
    }
  }

  return result;
}
